import 'react-phone-number-input/style.css'

import { CountryCode, getExampleNumber, parsePhoneNumber, PhoneNumber } from 'libphonenumber-js'
import examples from 'libphonenumber-js/mobile/examples'
import { FC, InputHTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { isValidPhoneNumber } from 'react-phone-number-input'
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form'

import { useLanguage } from '@/hooks/Contexts'
// override style from package
import style from '@/styles/PhoneNumberInput.module.css'
import { COUNTRY_LIST } from '@/utils/Consts'

import CaretIcon from '../CaretIcon'

export const PhoneNumberInput: FC<{
  isAddress: boolean
  form: UseFormReturn<any>
  extraCls?: string
  numberInputProps?: InputHTMLAttributes<HTMLInputElement>
  checkIconCls?: string
  validation?: boolean
}> = ({ isAddress, form, extraCls = '', numberInputProps, checkIconCls = '', validation = true }) => {
  const { user: userStr, errors: errStr } = useLanguage()
  const { control, getFieldState, getValues, setValue, trigger, watch } = form
  const defaultCountry: CountryCode = COUNTRY_LIST.find(({ name }) => name == getValues(isAddress ? 'address.country' : 'country'))?.code || 'IE'
  const [example, setExample] = useState<PhoneNumber>(getExampleNumber(defaultCountry, examples))
  useEffect(() => {
    if (!example && defaultCountry) {
      const exp = getExampleNumber(defaultCountry, examples)
      setExample(exp)
    }
  }, [])
  const placeholder = useMemo(() => example?.formatInternational?.()?.replace(`+${example?.countryCallingCode} `, ''), [example])
  const validate = useCallback(
    (value?: string) =>
      !example?.country
        ? 'Please select a country and enter a valid phone number'
        : !value
        ? 'Phone number is required'
        : isValidPhoneNumber(value, example?.country)
        ? null
        : `Please enter a valid phone number, e.g. +${example?.countryCallingCode} ${placeholder}`,
    [example]
  )
  const fieldName = useMemo(() => (isAddress ? 'address.phone' : 'phone_num'), [])
  const fieldState = getFieldState(fieldName)
  const showChecked = useMemo(() => fieldState?.isDirty && !fieldState?.error, [fieldState])

  /**
   * Keep the phone widget's country in sync with the country the user selects.
   * The phone field sits ABOVE the country dropdown, so it starts on the default
   * (IE) — most customers are Irish. When a customer then picks a different
   * country (e.g. United Kingdom), reparse the national number they already typed
   * into that country so a valid UK "07…" number isn't rejected as an Irish one,
   * and re-point validation at the new country. Irish customers who stay on IE
   * are unaffected (the early return skips everything).
   */
  const selectedCountryName = watch(isAddress ? 'address.country' : 'country')
  useEffect(() => {
    const code = COUNTRY_LIST.find(({ name }) => name === selectedCountryName)?.code
    if (!code || code === example?.country) return
    const current = getValues(fieldName)
    if (current) {
      try {
        const parsed = parsePhoneNumber(current)
        // Only "rescue" numbers that aren't valid as typed. A number that already parses as a
        // valid number for some other country is one the customer spelled out in full with its
        // dialling code — that's their real phone, and picking a billing country is not a
        // statement about it. Reparsing it silently swapped e.g. Cypriot +357 95122132 into
        // Irish +353 95122132 (a different, real subscriber) with no prompt.
        if (!parsed?.isValid() || parsed.country === code) {
          const nsn = parsed?.nationalNumber
          const reparsed = nsn ? parsePhoneNumber(nsn, code) : undefined
          // Only overwrite when the reparse yields a valid number for the new country;
          // otherwise leave the value so validation surfaces the error.
          if (reparsed?.isValid()) setValue(fieldName, reparsed.number, { shouldValidate: false })
        }
      } catch {
        // best-effort reformat; validation below still guards the final value
      }
    }
    setExample(getExampleNumber(code, examples))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryName])

  /**
   * show the sms entry checkbox for supported country, and
   * init checkbox value to ticked every time it's shown
   */
  useEffect(() => {
    if (isAddress) {
      trigger(fieldName)
      const newBool = ['GB', 'IE'].includes(example?.country)
      setValue('sms_entry', newBool, { shouldValidate: true })
      setValue('sms_subscribe', newBool, { shouldValidate: true })
    }
  }, [example?.country])

  return (
    <div className={`relative ${extraCls || ''}`}>
      <label className='font-medium text-black'>
        {userStr.phoneNum}
        <span className='ml-1 text-error'>*</span>
      </label>
      <PhoneInputWithCountry
        key={example?.country}
        name={fieldName}
        control={control}
        rules={{
          required: userStr.phoneNum + errStr.required,
          validate: validation ? validate : undefined,
          onChange: event => trigger(event?.target?.name || fieldName),
        }}
        numberInputProps={{ ...numberInputProps, className: 'mr-3' }}
        countrySelectProps={{
          arrowComponent: () => (
            <>
              <CaretIcon contentOpen={false} className='mx-1 h-4 w-4 !text-[#9ca3af]' />
              {example?.countryCallingCode ? <span className='mr-1'>+{example?.countryCallingCode}</span> : null}
              {showChecked ? <CheckIcon className={checkIconCls} /> : null}
            </>
          ),
        }}
        countryOptionsOrder={['GB', 'IE', '|', '...']}
        onCountryChange={country => {
          const exp = country ? getExampleNumber(country, examples) : null
          setExample(exp)
        }}
        focusInputOnCountrySelection={false}
        placeholder={placeholder}
        defaultCountry={example?.country || defaultCountry}
        countryCallingCodeEditable={false}
        international={false} // format input nationally
        className={`${
          style['phone-number-input']
        } relative mt-2 flex items-center rounded border-2 bg-white/100 px-2 py-2 shadow-lg shadow-stone-200 transition md:px-4 ${
          fieldState?.error ? 'border-error text-error' : 'border-stone-200 text-neutral-600 hover:border-cyan-v2 focus:border-cyan-v2'
        }`}
      />
      {fieldState?.error ? <div className='mt-1 text-xs text-error'>{fieldState?.error?.message}</div> : null}
    </div>
  )
}

const CheckIcon: FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <svg className={`absolute text-success ${className}`} width='14' height='14' viewBox='0 0 14 14' fill='currentColor'>
      <circle cx='7' cy='7' r='7' />
      <path d='M4 7.91638L6.08398 10L10 5' stroke='white' />
    </svg>
  )
}
export default PhoneNumberInput
